import { getDocs, getDoc } from 'firebase/firestore';
import {
  auditedAddDoc as addDoc,
  auditedUpdateDoc as updateDoc,
  auditedDeleteDoc as deleteDoc,
} from './auditedWrites';
import { deleteClientLogoStorage } from './storageUploadService';
import { getCareAppUserForPatientQueries } from './patientService';
import { getEffectiveTenantId, tenantCollection, tenantDoc } from './tenantContext';

const COLLECTION = 'clients';

export async function getClients() {
  const tid = await getEffectiveTenantId();
  if (!tid) return [];
  const snap = await getDocs(tenantCollection(tid, COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function findClientByName(name) {
  if (!name || !name.trim()) return [];
  const normalized = name.trim().toLowerCase();
  const all = await getClients();
  return all.filter((c) => (c.clientName || '').trim().toLowerCase() === normalized);
}

function buildClientDoc(data, tenantId) {
  if (!tenantId || typeof tenantId !== 'string') {
    throw new Error('tenantId is required.');
  }
  const contracts = Array.isArray(data.contracts) ? data.contracts : [];
  const clientShortName = (data.clientShortName || '').trim();
  if (!clientShortName) {
    throw new Error('Short name is required.');
  }
  return {
    tenantId,
    clientName: (data.clientName || '').trim(),
    clientShortName,
    clientAttention: (data.clientAttention || '').trim() || null,
    clientStreet1: (data.clientStreet1 || '').trim() || null,
    clientStreet2: (data.clientStreet2 || '').trim() || null,
    clientCity: (data.clientCity || '').trim() || null,
    clientState: (data.clientState || '').trim() || null,
    clientZip: (data.clientZip || '').trim() || null,
    status: (data.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
    logoUrl: (data.logoUrl || '').trim() || null,
    invoiceEmail1: (data.invoiceEmail1 || '').trim() || null,
    invoiceEmail2: (data.invoiceEmail2 || '').trim() || null,
    invoiceEmail3: (data.invoiceEmail3 || '').trim() || null,
    invoiceEmail4: (data.invoiceEmail4 || '').trim() || null,
    invoiceEmail5: (data.invoiceEmail5 || '').trim() || null,
    invoiceEmailCC1: (data.invoiceEmailCC1 || '').trim() || null,
    invoiceEmailCC2: (data.invoiceEmailCC2 || '').trim() || null,
    invoiceEmailCC3: (data.invoiceEmailCC3 || '').trim() || null,
    invoiceEmailCC4: (data.invoiceEmailCC4 || '').trim() || null,
    invoiceEmailCC5: (data.invoiceEmailCC5 || '').trim() || null,
    contracts: contracts.map((c) => ({
      serviceTypeId: c.serviceTypeId || null,
      rate: typeof c.rate === 'number' ? c.rate : parseFloat(c.rate) || 0,
      effectiveDate: c.effectiveDate || null,
      effectiveThruDate: c.effectiveThruDate || null,
    })),
  };
}

export async function createClient(data) {
  const user = await getCareAppUserForPatientQueries();
  let tid;
  if (user?.role === 'superuser' && user.impersonatingTenantId) {
    tid = user.impersonatingTenantId;
  } else if (user?.role === 'superuser' && data.tenantId) {
    tid = String(data.tenantId).trim();
  } else {
    tid = user?.tenantId;
  }
  if (!tid) throw new Error('Could not resolve tenant for this client.');
  const docData = buildClientDoc(data, tid);
  const ref = await addDoc(tenantCollection(tid, COLLECTION), docData);
  return ref.id;
}

export async function updateClient(id, data) {
  const tenantId = await getEffectiveTenantId();
  const snap = await getDoc(tenantDoc(tenantId, COLLECTION, id));
  if (!snap.exists()) throw new Error('Client not found.');
  const docData = buildClientDoc(data, tenantId);
  await updateDoc(tenantDoc(tenantId, COLLECTION, id), docData);
}

export async function deleteClient(id) {
  const tenantId = await getEffectiveTenantId();
  const snap = await getDoc(tenantDoc(tenantId, COLLECTION, id));
  const resolvedTenantId = snap.exists() ? snap.data()?.tenantId : tenantId;
  await deleteClientLogoStorage(id, resolvedTenantId);
  await deleteDoc(tenantDoc(tenantId, COLLECTION, id));
}

/** Check for overlapping contract date ranges for same serviceTypeId */
export function hasOverlappingContract(contracts, serviceTypeId, effectiveDate, effectiveThruDate, excludeIndex = -1) {
  const effective = effectiveDate ? new Date(effectiveDate).getTime() : 0;
  const thru = effectiveThruDate ? new Date(effectiveThruDate).getTime() : Number.MAX_SAFE_INTEGER;
  for (let i = 0; i < (contracts || []).length; i++) {
    if (i === excludeIndex) continue;
    const c = contracts[i];
    if ((c.serviceTypeId || '') !== (serviceTypeId || '')) continue;
    const cEffective = c.effectiveDate ? new Date(c.effectiveDate).getTime() : 0;
    const cThru = c.effectiveThruDate ? new Date(c.effectiveThruDate).getTime() : Number.MAX_SAFE_INTEGER;
    if (effective <= cThru && thru >= cEffective) return true;
  }
  return false;
}
